import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  Loader,
  Panel,
  SelectPicker,
  Table,
  Tag,
  Message,
  Stack,
} from "rsuite";
import AdminDashboard from "../../components/AdminDashboard";
import Header from "../../layouts/Header";
import PageBase from "../../components/PageBase";
import { CommissionPayment } from "../../my-type";
import { authUserData } from "../../service";
import {
  getCommissionPayments,
} from "../../service/CommissionPaymentService";

const statusOptions = [
  { label: "全部", value: "" },
  { label: "待轉帳", value: "pending" },
  { label: "已完成", value: "paid" },
];

const statusTagColorMap: Record<string, "orange" | "green" | "red" | "violet" | "blue"> = {
  pending: "orange",
  paid: "green",
  refund: "violet",
  cancelled: "red",
};

const statusLabelMap: Record<string, string> = {
  pending: "待轉帳",
  paid: "已完成",
  refund: "已退還",
  cancelled: "已取消",
};

const formatDateTime = (value: Date | string | null | undefined) => {
  if (!value) {
    return "-";
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("zh-TW", {
    timeZone: "Asia/Taipei",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const CommissionPaymentList: React.FC = () => {
  const { Column, HeaderCell, Cell } = Table;
  const authUser = authUserData();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [records, setRecords] = useState<CommissionPayment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadRecords = useCallback(async () => {
    if (!authUser?.id) {
      setErrorMessage("找不到登入使用者資料。");
      setLoading(false);
      return;
    }

    const teamCode = authUser?.team?.code;
    if (!teamCode) {
      setErrorMessage("找不到登入使用者的車隊資料。");
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage(null);

    try {
      const data = await getCommissionPayments(statusFilter || null, teamCode);
      const items = data ?? [];
      setRecords(items);
    } catch (error) {
      const message = error instanceof Error ? error.message : "取得佣金付款資料失敗";
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  }, [authUser?.id, authUser?.team?.code, statusFilter]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const header = (
    <Header
      HeaderTitle="佣金付款"
      isShowRefresh={false}
      OnReturn={() => window.history.back()}
      OnRefresh={loadRecords}
    />
  );

  return (
    <AdminDashboard title="佣金付款">
      <PageBase
        header={header}
        content={
          <>
            <Panel bordered header="篩選付款紀錄" style={{ marginBottom: 16 }}>
              <Stack spacing={12} alignItems="center" wrap>
                <div style={{ minWidth: 220 }}>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>狀態篩選</div>
                  <SelectPicker
                    aria-label="付款狀態篩選"
                    searchable={false}
                    cleanable={false}
                    data={statusOptions}
                    value={statusFilter}
                    onChange={(value) => setStatusFilter(value ?? "")}
                    style={{ width: "100%" }}
                  />
                </div>
                <Button appearance="default" onClick={loadRecords} disabled={loading}>
                  {loading ? "載入中..." : "重新整理"}
                </Button>
              </Stack>
            </Panel>

            {loading ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <Loader size="md" content="載入佣金付款資料中..." />
              </div>
            ) : errorMessage ? (
              <Message type="error" showIcon>
                {errorMessage}
              </Message>
            ) : records.length === 0 ? (
              <Message type="info" showIcon>
                查無佣金付款資料
              </Message>
            ) : (
              <Panel bordered header={`付款紀錄 · ${records.length} 筆`}>
                <Table
                  data={records}
                  autoHeight
                  bordered
                  cellBordered
                  hover={true}
                  onRowClick={(rowData) => navigate(`/commission-payments/${(rowData as CommissionPayment).orderID}`)}
                >
                  <Column width={100} fixed="right">
                    <HeaderCell>操作</HeaderCell>
                    <Cell>{(rowData: CommissionPayment) => <Link to={`/commission-payments/${rowData.orderID}`} onClick={event => event.stopPropagation()}>付款明細</Link>}</Cell>
                  </Column>
                  <Column width={180} fixed>
                    <HeaderCell>訂單編號</HeaderCell>
                    <Cell>
                      {(rowData: CommissionPayment) => (
                        <Link
                          to={`/orders/${rowData.orderID}`}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {rowData.orderID}
                        </Link>
                      )}
                    </Cell>
                  </Column>

                  <Column width={120} align="center">
                    <HeaderCell>狀態</HeaderCell>
                    <Cell>
                      {(rowData: CommissionPayment) => (
                        <Tag color={statusTagColorMap[rowData.status] ?? "blue"}>
                          {statusLabelMap[rowData.status] ?? rowData.status}
                        </Tag>
                      )}
                    </Cell>
                  </Column>

                  <Column width={120} align="right">
                    <HeaderCell>金額</HeaderCell>
                    <Cell>
                      {(rowData: CommissionPayment) => rowData.amount.toLocaleString()}
                    </Cell>
                  </Column>

                  <Column width={150}>
                    <HeaderCell>收款人</HeaderCell>
                    <Cell dataKey="payeeName" />
                  </Column>

                  <Column width={150}>
                    <HeaderCell>付款人</HeaderCell>
                    <Cell dataKey="payerName" />
                  </Column>

                  <Column flexGrow={1} minWidth={180}>
                    <HeaderCell>付款備註</HeaderCell>
                    <Cell dataKey="paymentReference" />
                  </Column>

                  <Column width={180}>
                    <HeaderCell>建立時間</HeaderCell>
                    <Cell>
                      {(rowData: CommissionPayment) => formatDateTime(rowData.createdAt)}
                    </Cell>
                  </Column>

                  <Column width={100} fixed="right" align="center">
                    <HeaderCell>操作</HeaderCell>
                    <Cell>
                      {(rowData: CommissionPayment) => (
                        <Button
                          size="xs"
                          appearance="ghost"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/commission-payments/${rowData.orderID}`);
                          }}
                        >
                          編輯
                        </Button>
                      )}
                    </Cell>
                  </Column>
                </Table>
              </Panel>
            )}
          </>
        }
        showSide={false}
      />
    </AdminDashboard>
  );
};

export default CommissionPaymentList;
